import {
  Component,
  OnDestroy,
  OnInit,
  Renderer2,
  DOCUMENT,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import {
  ActivationEnd,
  NavigationEnd,
  NavigationError,
  Router,
  Scroll,
} from '@angular/router';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs';
import { AuthenticationService } from './default/auth/services/authentication.service';

import { RaygunService } from './raygun/raygun.service';
import { CommonConstants } from './shared/constants/common.constant';
import { BrandId } from './shared/models/clux/enum';
import { BrandService } from './shared/services/brand.service';
import { SharedService } from './shared/services/shared.service';
import { PrefetchService } from './state/prefetch.service';
import { TimeoutService } from './timeout.service';

const anonymousPages: string[] = [
  CommonConstants.appRoutes.login,
  CommonConstants.appRoutes.logout,
  CommonConstants.appRoutes.signupBase,
  CommonConstants.appRoutes.twofaBase,
  CommonConstants.appRoutes.forgotpassword,
  CommonConstants.appRoutes.distributor,
  CommonConstants.appRoutes.twofactorAuthentication,
  CommonConstants.appRoutes.resetpassword,
  CommonConstants.appRoutes.nobrand,
  CommonConstants.appRoutes.netSuiteSSORedirect,
];

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RouterOutlet],
})
export class AppComponent implements OnInit, OnDestroy {
  private brandService = inject(BrandService);
  private document = inject<Document>(DOCUMENT);
  private matDialog = inject(MatDialog);
  private prefetchService = inject(PrefetchService);
  private raygunService = inject(RaygunService);
  private renderer = inject(Renderer2);
  private router = inject(Router);
  private sharedService = inject(SharedService);
  private title = inject(Title);
  private timeoutService = inject(TimeoutService);
  private authenticationService = inject(AuthenticationService);

  private readonly brand = this.brandService.getBrandResources();
  private readonly brandId = this.brandService.getBrand();
  private subscriptions = new Subscription();

  private timeoutSubscription = new Subscription();
  private idleSubscription = new Subscription();

  public ngOnInit(): void {
    const prefetchSubscription = this.authenticationService.authStatus$
      .pipe(
        distinctUntilChanged(),
        filter((status) => status === 'FULL_AUTH'),
        switchMap(() => this.prefetchService.prefetchDataOnAuthentication())
      )
      .subscribe();
    this.subscriptions.add(prefetchSubscription);

    this.timeoutService.setMaxIdleTime(location.hostname === 'localhost' ? 120 : 15);
    this.timeoutService.setMaxTimeoutTime(60 * 12);

    this.title.setTitle(this.brandId !== BrandId.Unknown ? `Welcome to ${this.brand.companyName}` : '');

    this.router.events
      .pipe(
        map((event) => {
          if (event instanceof NavigationError) {
            this.raygunService.logError('Navigation error', { event });
          }
          if (event instanceof ActivationEnd && event.snapshot.data.title) {
            this.title.setTitle(event.snapshot.data.title);
          }
          return event;
        }),
        filter((event): event is Scroll => event instanceof Scroll && event.routerEvent instanceof NavigationEnd),
        map((event: Scroll) => {
          this.renderer.setAttribute(this.document.getElementById('favicon'), 'href', `assets/img/${this.brand.faviconFileName}`);
          const currentRoute: string = event.routerEvent.url.split('/')[1].replace(/\?.+/, '');
          this.setLastRoute(event.routerEvent.url);

          this.stopIdleTimer();
          if (this.isAnonymousPage(currentRoute)) {
            // not to show multiple growel for non signIn component ;
            this.sharedService.growlProperties = [];
            this.stopTimeoutTimer();
            this.renderer.addClass(document.body, 'login');
          } else {
            this.startIdleTimer();
            this.startTimeoutTimer();
            this.renderer.removeClass(document.body, 'login');
          }
        })
      )
      .subscribe();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private stopIdleTimer(): void {
    this.idleSubscription.unsubscribe();
    this.timeoutService.stopIdleTimer();
  }

  private stopTimeoutTimer(): void {
    this.timeoutSubscription.unsubscribe();
    this.timeoutService.stopTimeoutTimer();
  }

  private startIdleTimer(): void {
    this.timeoutService.startIdleTimer();
    this.idleSubscription = this.timeoutService.watchIdle().subscribe(() => {
      void this.logout();
    });
    this.subscriptions.add(this.idleSubscription);
  }

  private startTimeoutTimer(): void {
    if (this.timeoutSubscription && !this.timeoutSubscription.closed) {
      this.timeoutSubscription.unsubscribe();
    }
    this.timeoutService.startTimeoutTimer();
    this.timeoutSubscription = this.timeoutService.watchTimeout().subscribe(() => {
      void this.logout();
    });
    this.subscriptions.add(this.timeoutSubscription);
  }

  /**
   * Returns true when the current page is one that does not require authentication (e.g. login/sign-up)
   * @param currentRoute The name of the current page
   */
  private isAnonymousPage(currentRoute: string): boolean {
    let returnBox: boolean;
    anonymousPages.forEach((element) => {
      if (element === currentRoute || currentRoute === '') {
        returnBox = true;
      } else {
        return false;
      }
    });
    return returnBox;
  }

  private setLastRoute(route: string): void {
    sessionStorage.setItem(CommonConstants.localKeys.userLastRoute, route);
  }

  private async logout(): Promise<void> {
    this.matDialog.closeAll();
    await this.router.navigate(['/logout']);
  }
}
